import axios, { AxiosInstance, AxiosResponse } from "axios";
import {
  ForgotPasswordProps,
  LoginApiProps,
  RegisterApiProps,
  ResetPasswordProps,
} from "./interfaces";
import { useMutation } from "@tanstack/react-query";
import { httpClient, ROOT_URL } from "../App";

enum ApiPathEnum {
  Login,
  Register,
  ResetPassword,
  ForgotPassword,
  ResendVerifyEmail,
}

export class SmartApi {
  rootUrl: string;
  urlMapper: Record<ApiPathEnum, string>;
  client: AxiosInstance;

  constructor(rootUrl: string) {
    this.rootUrl = rootUrl;
    this.urlMapper = {
      [ApiPathEnum.Login]: "/api/v1/auth/admin/login",
      [ApiPathEnum.Register]: "/api/v1/auth/register",
      [ApiPathEnum.ResetPassword]: "/api/v1/auth/reset-password",
      [ApiPathEnum.ForgotPassword]: "/api/v1/auth/forgot-password",
      [ApiPathEnum.ResendVerifyEmail]: "/api/v1/auth/resend-verify-email",
    };
    this.client = axios.create();
  }

  private buildUrl(path: ApiPathEnum): string {
    return `${this.rootUrl}${this.urlMapper[path]}`;
  }

  private async makeRequest(request: Promise<AxiosResponse>) {
    try {
      return await request;
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  async register(props: RegisterApiProps) {
    return await this.makeRequest(
      this.client.post(this.buildUrl(ApiPathEnum.Register), props)
    );
  }

  async login(props: LoginApiProps) {
    return await this.makeRequest(
      this.client.post(this.buildUrl(ApiPathEnum.Login), props)
    );
  }

  async resetPassword(props: ResetPasswordProps) {
    return await this.makeRequest(
      this.client.post(this.buildUrl(ApiPathEnum.ResetPassword), props)
    );
  }

  async forgotPassword(props: ForgotPasswordProps) {
    return await this.makeRequest(
      this.client.post(this.buildUrl(ApiPathEnum.ForgotPassword), props)
    );
  }

  async resendVerifyEmail(email: string) {
    return await this.makeRequest(
      this.client.post(this.buildUrl(ApiPathEnum.ResendVerifyEmail), { email })
    );
  }
}

export const useLogin = (config = {}) => {
  return useMutation({
    ...config,
    mutationFn: login,
  });
};

export function login(body: {
  email: string;
  password: string;
  isAdminLogin: boolean;
}) {
  return httpClient.post(`${ROOT_URL}/api/v1/auth/admin/login`, body);
}

export const useLogout = (config = {}) => {
  return useMutation({
    ...config,
    mutationFn: logout,
  });
};

export function logout() {
  return httpClient.post(`${ROOT_URL}/api/v1/auth/logout`);
}
